import React, {
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import HandsSvg from "@components/icons/hands-svg";
import RowKeyboard from "@components/row-keyboard/row-keyboard";

import { useDetectDevice } from "@hooks/use-detect-device";
import { isSameChars } from "@helpers/helpers";
import { keyboards } from "@constants/keyboards";
import {
  DeadKeyElements,
  KeyboardModifications,
  Keyboards,
} from "@typings/global.types";

export type KeyboardHandle = {
  focus: () => void;
  isFocused: () => boolean;
};

export type onKeyPressEvent = {
  isCorrect: boolean;
  altKey: boolean;
  shiftKey: boolean;
  capslockModifier: boolean;
  data: string;
  isCorrectDeadSymbol: boolean;
};

type PropsType = {
  currentSymbol: string;
  keyboardLayout: keyof Keyboards;
  onKeyPress: (e: onKeyPressEvent) => void;
  onKeyUp: (event: KeyboardEvent<HTMLInputElement>) => void;
  showUI?: boolean;
  showHands?: boolean;
  className?: string;
  shouldFocus?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onClickReload?: () => void;
};

export enum DeadSymbolByCode {
  "´" = "´",
  "`" = "`",
  "~" = "~",
  "^" = "^",
}

export const Board = forwardRef<KeyboardHandle, PropsType>(
  (
    {
      currentSymbol,
      keyboardLayout,
      onKeyPress,
      onKeyUp,
      showUI,
      showHands,
      className,
      shouldFocus,
      onFocus,
      onBlur,
      onClickReload,
    },
    ref,
  ) => {
    const [errorSymbol, setErrorSymbol] = useState<string | undefined>(
      undefined,
    );
    const [keyboardModification, setKeyboardModification] =
      useState<KeyboardModifications>("default");

    const { isTouchDevice } = useDetectDevice();

    const inputRef = useRef<HTMLInputElement>(null);

    const deadSymbolCode = useRef<string | undefined>(undefined);
    const deadSymbolRef = useRef<DeadSymbolByCode | undefined>(undefined);
    const afterDeadKeySymbolRef = useRef<boolean>(false);
    const isShiftRef = useRef(false);
    const isAltRef = useRef(false);
    const isCapslockRef = useRef(false);

    const keyboard = keyboards[keyboardLayout];
    const keyboardRows =
      keyboardModification in keyboard
        ? keyboard[keyboardModification]
        : keyboard.default;

    useImperativeHandle(ref, () => ({
      focus() {
        inputRef.current?.focus({
          preventScroll: true,
        });
      },
      isFocused() {
        return document.activeElement === inputRef.current;
      },
    }));

    const getKeyboardModification = () => {
      if (
        deadSymbolCode.current &&
        !deadSymbolRef.current &&
        keyboard.deadKeySpecialKeyNames
      ) {
        const deadSymbolValue =
          keyboard.deadKeySpecialKeyNames[
            deadSymbolCode.current as keyof typeof keyboard.deadKeySpecialKeyNames
          ];
        const deadSymbol = getDeadSymbol(`${deadSymbolValue}`);
        deadSymbolRef.current = deadSymbol;
      }

      return [
        deadSymbolRef.current,
        isAltRef.current && "alt",
        isShiftRef.current && "shift",
      ]
        .filter((item) => Boolean(item))
        .join("-") as KeyboardModifications;
    };

    const getDeadSymbol = (code: string) => {
      const deadSymbol = [code]
        .filter((item) => Boolean(item))
        .join("") as keyof typeof DeadSymbolByCode;

      if (deadSymbol in DeadSymbolByCode) {
        return DeadSymbolByCode[deadSymbol];
      }

      return undefined;
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        if (onClickReload) onClickReload();
      }
      const isDead = event.key === "Dead";

      isShiftRef.current = event.shiftKey;
      isAltRef.current = event.altKey;
      isCapslockRef.current = event.getModifierState("CapsLock");

      if (isDead) {
        deadSymbolCode.current = event.code;
      } else if (deadSymbolRef.current && !afterDeadKeySymbolRef.current) {
        afterDeadKeySymbolRef.current = true;
      }

      if (deadSymbolCode.current && event.code === "Backspace") {
        deadSymbolCode.current = undefined;
        deadSymbolRef.current = undefined;
        afterDeadKeySymbolRef.current = false;
        event.stopPropagation();
      }

      setKeyboardModification(getKeyboardModification());

      if (event.key === " ") {
        event.preventDefault();
        keyPressHandle(" ", false);
      }
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
      isShiftRef.current = event.shiftKey;
      isAltRef.current = event.altKey;
      setKeyboardModification(getKeyboardModification());
      setErrorSymbol(undefined);
      onKeyUp(event);
    };

    const handleInput = (event: React.CompositionEvent<HTMLInputElement>) => {
      const typedSymbol = event.nativeEvent.data?.charAt(
        event.nativeEvent.data.length - 1,
      );

      if (typedSymbol === " ") {
        keyPressHandle(" ", false);
        return;
      }

      const deadSymbol = getDeadSymbol(typedSymbol);
      let isCorrectDeadSymbol = false;
      if (deadSymbol) {
        deadSymbolRef.current = deadSymbol;
        if (keyboard?.deadKeyElements) {
          const deadKeySymbol = typedSymbol as keyof DeadKeyElements;
          if (keyboard.deadKeyElements[deadKeySymbol]) {
            isCorrectDeadSymbol =
              keyboard.deadKeyElements[deadKeySymbol]?.indexOf(
                currentSymbol,
              ) !== -1;
          } else {
            isCorrectDeadSymbol = false;
          }
        }
      }

      keyPressHandle(typedSymbol, isCorrectDeadSymbol);

      if (!isCorrectDeadSymbol) {
        deadSymbolCode.current = undefined;
        deadSymbolRef.current = undefined;
        // if (inputRef.current) {
        //   inputRef.current.value = Math.random().toString(36).slice(-1);
        // }
      }

      if (!deadSymbolRef.current || afterDeadKeySymbolRef.current) {
        afterDeadKeySymbolRef.current = false;
      }
    };

    const keyPressHandle = (
      typedSymbol: string,
      isCorrectDeadSymbol: boolean,
    ) => {
      const isCorrect = isSameChars(typedSymbol, currentSymbol);

      if (!isCorrect && !isCorrectDeadSymbol) {
        setErrorSymbol(typedSymbol);
      }

      onKeyPress({
        data: typedSymbol,
        isCorrect,
        shiftKey: isShiftRef.current,
        altKey: isAltRef.current,
        capslockModifier: isCapslockRef.current,
        isCorrectDeadSymbol,
      });
    };

    useEffect(() => {
      if (shouldFocus) {
        inputRef.current?.focus({
          preventScroll: true,
        });
      }
    }, [shouldFocus]);

    const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
      setKeyboardModification("default");
      if (onBlur) onBlur(e);
    };

    return (
      <div className={classNames("relative", className)}>
        <input
          ref={inputRef}
          style={{ height: 0, width: 0 }}
          className='absolute -top-[200px] overflow-hidden opacity-0'
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          // onBeforeInput={handleInput}
          onInput={handleInput}
          onFocus={onFocus}
          onBlur={handleOnBlur}
          autoComplete='off'
          autoCapitalize='off'
          inputMode='text'
          autoCorrect='off'
          spellCheck='false'
          type='text'
          aria-label='Text'
        />
        {showUI ? (
          <div
            className='relative'
            role='presentation'
            onClick={() => inputRef.current?.focus()}
          >
            {keyboardRows?.map((row, index) => (
              <RowKeyboard
                key={row}
                rowIndex={index}
                symbols={row}
                activeSymbol={currentSymbol}
                errorSymbol={errorSymbol}
                keyboardType={keyboardModification}
                keyboard={keyboard}
                deadSymbol={deadSymbolRef.current}
              />
            ))}
            {showHands ? (
              <div className='absolute -bottom-[62px] -left-[11px] flex w-full justify-center'>
                <HandsSvg />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);
